import { ReplayJournal } from '@/components/admin/reglages/replay-journal';
import { ReplaySettingsPanel } from '@/components/admin/reglages/replay-settings-panel';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { fetchModelCatalog, fetchModelParams } from '@/lib/api';
import { catalogFrom, catalogSourceSentence } from '@/lib/model-catalog';
import { playerOf, saveFailureSentence } from '@/lib/replay-settings';
import { saveReplaySettings } from '@/lib/replay-settings-api';
import {
  REPLAY_SETTINGS_CHANGES_KEY,
  REPLAY_SETTINGS_KEY,
  useReplaySettings,
  useReplaySettingsChanges,
} from '@/lib/use-replay-settings';
import type { ModelCatalogView, ReplaySettingsInput } from '@matter_extractor/shared';
import { Spinner } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Réglages > Rejeu (docs/specs/rejeu.md §10.3, issue #193): the EIGHTH tab of
 * Réglages, after Méthode (#196, which came in right after Noyaux) - the three
 * texts of the instruction Claude follows when it plays the person of a replay,
 * and the journal of their saves.
 *
 * ## The tab exists only when the replay is open (§4)
 *
 * `GET /api/replay-settings` answers 404 on a server where `REPLAY_ENABLED` is
 * not `true`, and the shell draws the tab from the same read. A direct visit to
 * this address on such a server therefore lands on the state « introuvable » the
 * administration gives every address that names nothing - not on an empty
 * editor whose save could only fail.
 *
 * ## The model of the player, over the texts (npc.md §13.4, issue #210)
 *
 * The tab also holds the model a replay the SERVER plays runs on by default: a block over the three
 * texts, with the fields of a seat of Réglages > Modèles, drawn by the same card. The tiles and the
 * published rates it offers are those of that screen, read under the same keys
 * (`['model-catalog', email]`, `['model-params', email]`) and never waited on: until they land the
 * block draws on the built-in list, and says so - or, if they never do, says that too. The model
 * travels with the texts in « Enregistrer et journaliser » and is written to the journal beside them.
 *
 * ## Not versioned, but journalled
 *
 * Unlike the texts of the noyaux there is no history to walk back here: the
 * save overwrites, and the journal is what keeps the move. Each replay freezes
 * its own copy of the instruction when it starts, so retouching these texts
 * touches no replay already begun - the page says so, and so does the box that
 * announces the save.
 *
 * ## One key per save, and the panel remounts on it
 *
 * The régime of `reglages.regles.tsx`: the panel seeds its draft from its props
 * and never by an effect, so a save that LANDED remounts it on what the base
 * now holds (the draft has just become the text in force). A save that failed
 * does not: the three paragraphs the operator typed are the only place they
 * exist, and the failure is said under the box with the button still live.
 */

export const Route = createFileRoute('/admin/reglages/rejeu')({
  component: ReglagesRejeuPage,
});

/**
 * EXPORTED for `tests/reglages-rejeu.test.tsx`, and for nothing else:
 * `vitest.config.ts` leaves `src/routes/**` out of the coverage REPORT, not out
 * of the suite, and what this component decides - the closed state, the
 * invalidations after a save, the remount - cannot be seen from the panel.
 */
export function ReglagesRejeuPage() {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const [generation, setGeneration] = useState(0);

  const settings = useReplaySettings();
  // The journal is read once the replay is known to be open: on a closed
  // server it would only collect a second 404.
  const changes = useReplaySettingsChanges(settings.data != null);

  // The tiles and the rates of the player's block: the keys of Réglages > Modèles, so a visit to
  // either warms the other. Never waited on: a failed read leaves the block on the built-in list.
  const models = useQuery({
    queryKey: ['model-catalog', email],
    queryFn: () => fetchModelCatalog(),
    retry: false,
    staleTime: 60 * 60 * 1000,
    enabled: settings.data != null,
  });
  const rates = useQuery({
    queryKey: ['model-params', email],
    queryFn: () => fetchModelParams(),
    retry: false,
    enabled: settings.data != null,
  });

  const save = useMutation({
    mutationFn: (input: ReplaySettingsInput) => saveReplaySettings(input),
    onSuccess: async (saved) => {
      // The answer IS the new state of the base: written to the cache first so
      // the remounted panel is seeded on it, then both reads are made fresh -
      // the journal gained a line and `replayCount` may have moved.
      queryClient.setQueryData([REPLAY_SETTINGS_KEY, email], saved);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [REPLAY_SETTINGS_KEY] }),
        queryClient.invalidateQueries({ queryKey: [REPLAY_SETTINGS_CHANGES_KEY] }),
      ]);
      setGeneration((count) => count + 1);
    },
  });

  // A 401 is the door reopening, not an error of this screen (T6).
  const rejected = useRejectOn401(settings.error);

  if (settings.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner label="Chargement de la consigne du rejeu" />
      </div>
    );
  }

  // The replay is closed on this server: the tab does not exist, and neither
  // does this address. Same sentence and same way out as the editors.
  if (settings.data === null) {
    return (
      <p data-testid="rejeu-introuvable" className="text-gray-700">
        Cette page n’existe pas : le rejeu n’est pas ouvert sur ce serveur.{' '}
        <Link to="/admin/reglages/modeles" className="text-bleu-nuit underline underline-offset-2">
          Revenir aux réglages
        </Link>
        .
      </p>
    );
  }

  if (settings.data === undefined) {
    return rejected ? null : (
      <p role="alert" className="text-rouge-deep text-small">
        La consigne du rejeu n’a pas pu être lue. Rechargez l’écran.
      </p>
    );
  }

  // A failed read is said as one - both doors unread - and not as a read still in flight, which
  // is what an absent view would claim forever (the page of Réglages > Modèles does the same).
  const catalogView: ModelCatalogView | undefined = models.isError
    ? {
        doors: { anthropic: 'unavailable', openrouter: 'unavailable' },
        fetchedAt: null,
        entries: [],
      }
    : models.data;

  return (
    <section data-testid="reglages-rejeu" className="flex flex-col gap-5 pb-16">
      <header className="flex flex-col gap-1.5">
        <h2 className="font-semibold text-[22px] text-gray-900 leading-[30px]">Rejeu</h2>
        <p className="max-w-[96ch] text-gray-600 text-small leading-[21px]">
          Le joueur d’un rejeu et sa consigne : Claude par l’assistant MCP de ce serveur, ou le
          joueur du serveur, lancé depuis un NPC ou un banc. La consigne sert aux deux. Elle est
          servie dans cet ordre : les règles communes, le paragraphe du mode choisi au démarrage,
          puis le NPC et ses sources, ou la source collée.{' '}
          <strong className="font-semibold text-gray-700">Chaque rejeu en garde une copie</strong> :
          retoucher ces textes ne change aucun rejeu commencé. Pas de versions ici, mais un journal
          de chaque enregistrement.
        </p>
      </header>

      <ReplaySettingsPanel
        // The key is the generation of the LAST SAVE THAT LANDED, never the
        // data itself: a refetch under a typing hand must not remount.
        key={generation}
        settings={settings.data}
        saving={save.isPending}
        failure={save.isError ? saveFailureSentence(save.error) : null}
        catalog={catalogFrom(catalogView, [playerOf(settings.data)])}
        tariffs={rates.data?.tariffs ?? []}
        catalogSource={catalogSourceSentence(catalogView)}
        onSave={(input) => save.mutate(input)}
        onRevert={() => save.reset()}
      />

      {changes.data === undefined ? (
        <JournalPending failed={changes.isError} />
      ) : (
        <ReplayJournal changes={changes.data.changes} />
      )}
    </section>
  );
}

/** The journal is still being read, or could not be: the texts above are not held back for it. */
function JournalPending({ failed }: { readonly failed: boolean }) {
  return failed ? (
    <p role="alert" data-testid="rejeu-journal-echec" className="text-rouge-deep text-small">
      Le journal n’a pas pu être lu. Rechargez l’écran.
    </p>
  ) : (
    <div className="flex justify-center py-6">
      <Spinner label="Chargement du journal" />
    </div>
  );
}
